import { Component, input, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DecimalPipe, DatePipe } from '@angular/common';
import { ProductionKeyPerformanceIndicatorEvaluationEnum } from 'src/app/enums/production-key-performance-indicator-evaluation.enum';
import { ProductionKeyPerformanceIndicatorThresholdValueTypeEnum } from 'src/app/enums/production-key-performance-indicator-threshold-value-type.enum';
import {
  ProductionKeyPerformanceIndicatorEvaluation,
  ProductionKeyPerformanceIndicatorEvaluationSummary,
  ProductionKeyPerformanceIndicatorEvaluationWithData,
  getKpiEvaluationStyle,
  KpiEvaluationStyle,
} from 'src/app/models/production-key-performance-indicator-evaluation.model';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RegisteredWorkComplianceDetailsComponent } from './registered-work-compliance-details/registered-work-compliance-details.component';
import { ScheduleAndRemoteWorkComplianceDetailsComponent } from './schedule-and-remote-work-compliance-details/schedule-and-remote-work-compliance-details.component';
import { SharePresenceWithColleaguesDetailsComponent } from './share-presence-with-colleagues-details/share-presence-with-colleagues-details.component';
import { MeetingAttendanceDetailsComponent } from './meeting-attendance-details/meeting-attendance-details.component';
import { ConfiguredApplicationsDetailsComponent } from './configured-applications-details/configured-applications-details.component';
import { SprintPlanningExecutionDetailsComponent } from './sprint-planning-execution-details/sprint-planning-execution-details.component';
import { SprintReviewExecutionDetailsComponent } from './sprint-review-execution-details/sprint-review-execution-details.component';
import { CompanyProductDailyStandupComplianceDetailsComponent } from './company-product-daily-standup-compliance-details/company-product-daily-standup-compliance-details.component';
import { CompanyProductDeploysFrequencyDetailsComponent } from './company-product-deploys-frequency-details/company-product-deploys-frequency-details.component';
import { UptimeMonitorsDetailsComponent } from './uptime-monitors-details/uptime-monitors-details.component';
import { ProductionKeyPerformanceIndicatorTypeEnum } from 'src/app/enums/production-key-performance-indicator-type.enum';
import { CompanyProductExpectedVsRegisteredWorkDetailsComponent } from './company-product-expected-vs-registered-work-details/company-product-expected-vs-registered-work-details.component';
import { CompanyProductPhaseTimeComplianceDetailsComponent } from './company-product-phase-time-compliance-details/company-product-phase-time-compliance-details.component';
import { HoldedConfiguredDetailsComponent } from './holded-configured-details/holded-configured-details.component';
import { HoldedConfiguredKpiData } from 'src/app/models/holded-configured-kpi-data.model';
import { IssuesRatioCheckDetailsComponent } from './issues-ratio-check-details/issues-ratio-check-details.component';
import { TasksReviewedByColleagueDetailsComponent } from './tasks-reviewed-by-colleague-details/tasks-reviewed-by-colleague-details.component';
import { ProductTrioTaskCreationDetailsComponent } from './product-trio-task-creation-details/product-trio-task-creation-details.component';

@Component({
  selector: 'app-kpi-evaluation-card',
  imports: [
    CommonModule,
    DecimalPipe,
    DatePipe,
    MatIconModule,
    MatTooltipModule,
    RegisteredWorkComplianceDetailsComponent,
    ScheduleAndRemoteWorkComplianceDetailsComponent,
    SharePresenceWithColleaguesDetailsComponent,
    MeetingAttendanceDetailsComponent,
    ConfiguredApplicationsDetailsComponent,
    SprintReviewExecutionDetailsComponent,
    CompanyProductDailyStandupComplianceDetailsComponent,
    CompanyProductDeploysFrequencyDetailsComponent,
    UptimeMonitorsDetailsComponent,
    SprintPlanningExecutionDetailsComponent,
    CompanyProductExpectedVsRegisteredWorkDetailsComponent,
    CompanyProductPhaseTimeComplianceDetailsComponent,
    HoldedConfiguredDetailsComponent,
    IssuesRatioCheckDetailsComponent,
    TasksReviewedByColleagueDetailsComponent,
    ProductTrioTaskCreationDetailsComponent,
  ],
  templateUrl: './kpi-evaluation-card.component.html',
  styleUrl: './kpi-evaluation-card.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiEvaluationCardComponent {
  // Estructuramos la información del KPI
  kpiData = input.required<ProductionKeyPerformanceIndicatorEvaluationWithData>();

  currentMonthEvaluation = computed((): ProductionKeyPerformanceIndicatorEvaluation | null => {
    return this.kpiData().data?.[0] ?? null;
  });
  isConfiguredApplications = computed(
    (): boolean => this.kpiData().production_key_performance_indicator.slug === 'configured-applications'
  );

  isHoldedConfigured = computed((): boolean => this.kpiData().production_key_performance_indicator.slug === 'holded-configured');

  private configuredApplicationsStats = computed(
    (): {
      total: number;
      configured: number;
      unconfigured: number;
      acceptable: number;
      optimal: number;
    } | null => {
      if (!this.isConfiguredApplications()) {
        return null;
      }

      const rawData = this.currentMonthEvaluation()?.data;
      const data = ConfiguredApplicationsDetailsComponent.isConfiguredApplicationsEvaluationData(rawData) ? rawData : null;
      if (data === null) {
        return null;
      }

      const total = data.total_applications;
      const unconfigured = data.unconfigured_applications_count;
      const configured = data.configured_applications_count;
      const optimal = total;
      const acceptable = Math.max(0, total - 1);

      return { total, configured, unconfigured, acceptable, optimal };
    }
  );

  private holdedConfiguredStats = computed(
    (): {
      total: number;
      configured: number;
      optimal: number;
    } | null => {
      if (!this.isHoldedConfigured()) {
        return null;
      }

      const rawData = this.currentMonthEvaluation()?.data;
      const data: HoldedConfiguredKpiData | null = HoldedConfiguredDetailsComponent.isHoldedConfiguredKpiData(rawData) ? rawData : null;
      if (data === null) {
        return null;
      }

      return {
        total: data.total_fields_count,
        configured: data.configured_fields_count,
        optimal: data.total_fields_count,
      };
    }
  );

  hasException = computed((): boolean => this.kpiData().has_exception);
  exceptionMessage = computed((): string => 'Este indicador tiene una excepción activa.');

  // Thresholds dinámicos desde las variable conditions
  mediumThreshold = computed((): number => {
    const holdedStats = this.holdedConfiguredStats();
    if (holdedStats !== null) {
      return holdedStats.optimal;
    }

    const configuredStats = this.configuredApplicationsStats();
    if (configuredStats !== null) {
      return configuredStats.acceptable;
    }

    const conditions = this.kpiData().production_key_performance_indicator.production_key_performance_indicator_variable_conditions;
    return conditions?.[0]?.acceptable_threshold;
  });

  goodThreshold = computed((): number | null => {
    const holdedStats = this.holdedConfiguredStats();
    if (holdedStats !== null) {
      return holdedStats.optimal;
    }

    const configuredStats = this.configuredApplicationsStats();
    if (configuredStats !== null) {
      return configuredStats.optimal;
    }

    const conditions = this.kpiData().production_key_performance_indicator.production_key_performance_indicator_variable_conditions;
    const optimalThreshold = conditions?.[0]?.optimal_threshold;
    if (optimalThreshold !== null && optimalThreshold !== undefined) {
      return optimalThreshold;
    }

    return null;
  });

  hasGoodThreshold = computed((): boolean => this.goodThreshold() !== null);

  thresholdValueType = computed((): ProductionKeyPerformanceIndicatorThresholdValueTypeEnum => {
    const conditions = this.kpiData().production_key_performance_indicator.production_key_performance_indicator_variable_conditions;
    return conditions?.[0]?.threshold_value_type ?? ProductionKeyPerformanceIndicatorThresholdValueTypeEnum.PERCENT;
  });

  // Iconos de KPIs dinámicos
  kpiIcons: Record<string, string> = {
    'schedule-and-remote-work-compliance': 'access_time',
    'registered-work-compliance': 'playlist_add_check',
    'share-presence-with-colleagues': 'people',
    'meeting-attendance': 'meeting_room',
    'configured-applications': 'apps',
    'sprint-review-execution': 'groups',
    'company-product-daily-standup-compliance': 'event_repeat',
    'company-product-deploys-frequency': 'rocket_launch',
    'uptime-monitors': 'monitor_heart',
    'sprint-planning-execution': 'engineering',
    'product-trio-task-creation': 'diversity_3',
    'company-product-expected-vs-registered-work': 'compare_arrows',
    'company-product-phase-time-compliance': 'timeline',
    'holded-configured': 'account_balance',
    'issues-ratio-check': 'bug_report',
    'tasks-reviewed-by-colleague': 'fact_check',
  };

  kpiIcon = computed((): string | undefined => {
    const slug = this.kpiData().production_key_performance_indicator.slug;
    return this.kpiIcons[slug];
  });

  // Detectar tipo de KPI
  isRegisteredWorkCompliance = computed((): boolean => {
    return this.kpiData().production_key_performance_indicator.slug === 'registered-work-compliance';
  });

  isScheduleAndRemoteWorkCompliance = computed((): boolean => {
    return this.kpiData().production_key_performance_indicator.slug === 'schedule-and-remote-work-compliance';
  });

  isSharePresenceWithColleagues = computed((): boolean => {
    return this.kpiData().production_key_performance_indicator.slug === 'share-presence-with-colleagues';
  });

  isMeetingAttendance = computed((): boolean => {
    return this.kpiData().production_key_performance_indicator.slug === 'meeting-attendance';
  });

  isConfiguredApplicationsKpi = computed((): boolean => {
    return this.kpiData().production_key_performance_indicator.slug === 'configured-applications';
  });

  isCompanyProductDailyStandupCompliance = computed((): boolean => {
    return this.kpiData().production_key_performance_indicator.slug === 'company-product-daily-standup-compliance';
  });

  isCompanyProductDeploysFrequency = computed((): boolean => {
    return this.kpiData().production_key_performance_indicator.slug === 'company-product-deploys-frequency';
  });

  isCompanyProductExpectedVsRegisteredWork = computed((): boolean => {
    return this.kpiData().production_key_performance_indicator.slug === 'company-product-expected-vs-registered-work';
  });

  isCompanyProductPhaseTimeCompliance = computed((): boolean => {
    return this.kpiData().production_key_performance_indicator.slug === 'company-product-phase-time-compliance';
  });

  isSprintReviewExecution = computed((): boolean => {
    return this.kpiData().production_key_performance_indicator.slug === 'sprint-review-execution';
  });

  isUptimeMonitors = computed((): boolean => {
    return this.kpiData().production_key_performance_indicator.slug === 'uptime-monitors';
  });

  isIssuesRatioCheck = computed((): boolean => {
    return this.kpiData().production_key_performance_indicator.slug === 'issues-ratio-check';
  });

  isSprintPlanningExecution = computed((): boolean => {
    return this.kpiData().production_key_performance_indicator.slug === 'sprint-planning-execution';
  });

  isTasksReviewedByColleague = computed((): boolean => {
    return this.kpiData().production_key_performance_indicator.slug === 'tasks-reviewed-by-colleague';
  });

  isProductTrioTaskCreation = computed((): boolean => {
    return this.kpiData().production_key_performance_indicator.slug === 'product-trio-task-creation';
  });

  usesAbsoluteThresholds = computed((): boolean => {
    const conditions = this.kpiData().production_key_performance_indicator.production_key_performance_indicator_variable_conditions;
    return conditions?.[0]?.threshold_value_type === ProductionKeyPerformanceIndicatorThresholdValueTypeEnum.ABSOLUTE;
  });

  lastMonthEvaluationStyle = computed((): KpiEvaluationStyle => {
    const data = this.currentMonthEvaluation();
    const evaluation = data?.evaluation ?? ProductionKeyPerformanceIndicatorEvaluationEnum.NotCalculable;
    return getKpiEvaluationStyle(evaluation);
  });

  isNotCalculable = computed(
    (): boolean => this.currentMonthEvaluation()?.evaluation === ProductionKeyPerformanceIndicatorEvaluationEnum.NotCalculable
  );

  lastMonthValue = computed((): number => {
    const holdedStats = this.holdedConfiguredStats();
    if (holdedStats !== null) {
      return holdedStats.configured;
    }

    const configuredStats = this.configuredApplicationsStats();
    if (configuredStats !== null) {
      return configuredStats.configured;
    }

    const percentage = this.currentMonthEvaluation()?.percentage;
    return percentage ?? 0;
  });

  valueSuffix = computed((): string => {
    return this.thresholdValueType() === ProductionKeyPerformanceIndicatorThresholdValueTypeEnum.PERCENT ? '%' : '';
  });

  thresholdTooltipText = computed((): string => {
    const holdedStats = this.holdedConfiguredStats();
    if (holdedStats !== null) {
      return `Campos Holded configurados: ${holdedStats.configured}/${holdedStats.total} - Requerido: ${holdedStats.optimal}/${holdedStats.total}`;
    }

    const configuredStats = this.configuredApplicationsStats();
    if (configuredStats !== null) {
      return `Aplicaciones configuradas: ${configuredStats.configured}/${configuredStats.total} - Aceptable: ${configuredStats.acceptable} - Óptimo: ${configuredStats.optimal}`;
    }

    return this.hasGoodThreshold()
      ? `Umbral Aceptable: ${Math.round(this.mediumThreshold())}${this.valueSuffix()} - Umbral Óptimo: ${Math.round(this.goodThreshold() ?? 0)}${this.valueSuffix()}`
      : `Umbral Aceptable: ${Math.round(this.mediumThreshold())}${this.valueSuffix()}`;
  });

  progressScaleMax = computed((): number | null => {
    if (this.thresholdValueType() === ProductionKeyPerformanceIndicatorThresholdValueTypeEnum.PERCENT) {
      return 100;
    }

    return null;
  });

  private toPositionPercent(value: number): number | null {
    const max = this.progressScaleMax();

    if (max === null) {
      return null;
    }

    return Math.max(0, Math.min(100, (value / max) * 100));
  }

  progressValuePosition = computed((): number | null => this.toPositionPercent(this.lastMonthValue()));
  mediumThresholdPosition = computed((): number | null => this.toPositionPercent(this.mediumThreshold()));
  goodThresholdPosition = computed((): number | null => this.toPositionPercent(this.goodThreshold() ?? 0));

  progressBarColor = computed((): string => this.lastMonthEvaluationStyle().color);

  historyDataWithStyle = computed(
    (): Array<(ProductionKeyPerformanceIndicatorEvaluationSummary & { style: KpiEvaluationStyle }) | null> => {
      const history = (this.kpiData().data?.slice(1, 7) ?? []) as ProductionKeyPerformanceIndicatorEvaluationSummary[];
      const emptySlots = 6 - history.length;
      const enriched = history.map(month => ({
        ...month,
        style: getKpiEvaluationStyle(month.evaluation),
      }));
      return [...enriched, ...Array(emptySlots).fill(null)];
    }
  );

  consecutiveAcceptableCount = computed((): number => {
    const data = this.kpiData().data ?? [];
    let count = 0;
    for (const month of data) {
      if (month.evaluation === ProductionKeyPerformanceIndicatorEvaluationEnum.Medium) {
        count += 1;
      } else {
        break;
      }
    }
    return count;
  });

  shouldShowConsecutiveMediumTooltip = computed((): boolean => {
    return this.currentMonthEvaluation()?.evaluation === ProductionKeyPerformanceIndicatorEvaluationEnum.Medium;
  });

  consecutiveMediumTooltipMessage = computed((): string => {
    const pkpi = this.kpiData().production_key_performance_indicator;
    const conditions = this.kpiData().production_key_performance_indicator.production_key_performance_indicator_variable_conditions;
    const nConsecutive = conditions?.[0]?.n_consecutive_medium_equals_poor ?? 3;
    const value = Math.round(this.lastMonthValue());
    const suffix = this.valueSuffix();
    const count = this.consecutiveAcceptableCount();
    const label = this.lastMonthEvaluationStyle().label.toLowerCase();

    if (
      pkpi.type === ProductionKeyPerformanceIndicatorTypeEnum.PERSONAL ||
      pkpi.type === ProductionKeyPerformanceIndicatorTypeEnum.COMPANY_PRODUCT_PERSONAL
    ) {
      if (count === 1) {
        return `Tu rendimiento este mes es mejorable (${value}${suffix}). Si tienes ${nConsecutive} evaluaciones consecutivas mejorables, la evaluación se marcará como insuficiente y afectará a tu sueldo.`;
      }

      return `Es la ${count === 2 ? 'segunda' : `${count}ª`} vez consecutiva con rendimiento mejorable (${value}${suffix}). A la próxima tu evaluación bajará a insuficiente y afectará a tu sueldo.`;
    }

    return `El rendimiento del producto es ${label}. Si se llega a ${nConsecutive} evaluaciones consecutivas ${label}s, la evaluación se marcará como insuficiente.`;
  });
}
